
import React, { createContext, useContext, useState, useCallback } from 'react';
import { CheckCircle2, Info, AlertCircle, X } from 'lucide-react';

type ToastType = 'success' | 'info' | 'error';

interface Toast {
  type: ToastType;
  text: string;
}

interface ToastContextType {
  showToast: (type: ToastType, text: string, onExited?: () => void) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) throw new Error('useToast must be used within a ToastProvider');
  return context;
};

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toast, setToast] = useState<(Toast & { onExited?: () => void }) | null>(null);

  const showToast = useCallback((type: ToastType, text: string, onExited?: () => void) => {
    setToast({ type, text, onExited });
    setTimeout(() => {
      setToast(prev => {
        if (prev?.onExited) prev.onExited();
        return null;
      });
    }, 2100);
  }, []);

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      {toast && (
        <div className="fixed bottom-20 md:bottom-10 right-6 z-[200] w-[calc(100%-48px)] max-w-[340px] animate-in slide-in-from-right-4 fade-in duration-500">
          <div className="bg-white/90 backdrop-blur-xl border border-slate-100 rounded-2xl shadow-[0_20px_50px_rgba(0,0,0,0.1)] overflow-hidden ring-1 ring-black/5 flex flex-col">
            <div className="p-4 flex items-center gap-4">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${
                toast.type === 'success' ? 'bg-emerald-50 text-emerald-600' : 
                toast.type === 'info' ? 'bg-blue-50 text-blue-600' : 'bg-rose-50 text-rose-600'
              }`}>
                {toast.type === 'success' ? <CheckCircle2 size={24} strokeWidth={2.5} /> : 
                 toast.type === 'info' ? <Info size={24} strokeWidth={2.5} /> : <AlertCircle size={24} strokeWidth={2.5} />}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-black text-slate-900 uppercase tracking-tight leading-tight truncate">
                  {toast.text}
                </p>
                <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mt-0.5">
                  System Intel Updated
                </p>
              </div>
              <button 
                onClick={() => {
                  if (toast.onExited) toast.onExited();
                  setToast(null);
                }} 
                className="text-slate-300 hover:text-slate-900 transition-colors p-1"
              >
                <X size={16} strokeWidth={3} />
              </button>
            </div>
            <div className="w-full h-1 bg-slate-50 relative">
              <div className={`h-full animate-toast-progress ${
                toast.type === 'success' ? 'bg-emerald-500' : 
                toast.type === 'info' ? 'bg-blue-600' : 'bg-rose-500'
              }`} />
            </div>
          </div>
        </div>
      )}
    </ToastContext.Provider>
  );
};
